import { useState } from 'react';
import { Shield, ChevronRight, Search, Lock, Eye, UserCheck, FileCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import Footer from '@/components/ui/Footer';

interface PrivacySection {
  id: string;
  title: string;
  description: string;
  content: React.ReactNode;
}

export default function Privacy() {
  const [activeSection, setActiveSection] = useState('data-collection');
  const [searchTerm, setSearchTerm] = useState('');

  const privacySections: PrivacySection[] = [
    {
      id: 'data-collection',
      title: 'Recolección de Datos',
      description: 'Información sobre los datos que recopilamos.',
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-semibold text-slate-900 mb-4">Recolección de Datos</h3>
          <p className="text-slate-600 leading-relaxed">
            En Validea, nos tomamos muy en serio la privacidad de nuestros usuarios. Recopilamos información para mejorar
            nuestros servicios y proporcionar una mejor experiencia de usuario.
          </p>
          
          <div className="mt-6 space-y-4">
            <h4 className="text-lg font-medium text-slate-900">Datos que Recopilamos</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: 'Información de Cuenta',
                  items: ['Nombre', 'Correo electrónico', 'Contraseña encriptada']
                },
                {
                  title: 'Datos de MVP',
                  items: ['Nombre del MVP', 'Descripción', 'Configuración']
                },
                {
                  title: 'Métricas',
                  items: ['Visitas', 'Interacciones', 'Conversiones']
                },
                {
                  title: 'Información Técnica',
                  items: ['Dirección IP', 'Tipo de navegador', 'Sistema operativo']
                }
              ].map((category, index) => (
                <div key={index} className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h5 className="font-medium text-slate-900 mb-2">{category.title}</h5>
                  <ul className="space-y-1">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="text-sm text-slate-600 flex items-center gap-2">
                        <div className="w-1 h-1 rounded-full bg-slate-400"></div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'data-usage',
      title: 'Uso de Datos',
      description: 'Cómo utilizamos la información recopilada.',
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-semibold text-slate-900 mb-4">Uso de Datos</h3>
          <p className="text-slate-600 leading-relaxed">
            Utilizamos los datos recopilados para diversos propósitos, siempre enfocados en mejorar nuestros servicios
            y la experiencia del usuario.
          </p>

          <div className="space-y-4">
            {[
              {
                title: 'Mejora del Servicio',
                description: 'Analizamos patrones de uso para optimizar nuestras funcionalidades.'
              },
              {
                title: 'Personalización',
                description: 'Adaptamos la experiencia según las preferencias del usuario.'
              },
              {
                title: 'Comunicación',
                description: 'Enviamos actualizaciones importantes y notificaciones relevantes.'
              },
              {
                title: 'Seguridad',
                description: 'Protegemos las cuentas y prevenimos actividades fraudulentas.'
              }
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-4 p-4 bg-white rounded-lg border border-slate-200">
                <Shield className="w-5 h-5 text-primary-600 flex-shrink-0 mt-1" />
                <div>
                  <h5 className="font-medium text-slate-900">{item.title}</h5>
                  <p className="text-sm text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: 'data-protection',
      title: 'Protección de Datos',
      description: 'Medidas de seguridad implementadas.',
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-semibold text-slate-900 mb-4">Protección de Datos</h3>
          <p className="text-slate-600 leading-relaxed">
            Implementamos medidas de seguridad técnicas y organizativas para proteger sus datos personales.
          </p>

          <div className="bg-primary-50 border border-primary-100 rounded-lg p-6">
            <h4 className="text-lg font-medium text-primary-900 mb-4">Medidas de Seguridad</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                'Encriptación de datos sensibles',
                'Monitoreo continuo de seguridad',
                'Backups automáticos',
                'Control de acceso estricto',
                'Auditorías regulares',
                'Actualizaciones de seguridad'
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-primary-200">
                  <Shield className="w-4 h-4 text-primary-600" />
                  <span className="text-sm text-primary-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'data-rights',
      title: 'Derechos del Usuario',
      description: 'Tus derechos sobre tus datos personales.',
      content: (
        <div className="space-y-6">
          <h3 className="text-2xl font-semibold text-slate-900 mb-4">Derechos del Usuario</h3>
          <p className="text-slate-600 leading-relaxed">
            Como usuario de Validea, tienes derechos específicos sobre tus datos personales.
          </p>

          <div className="space-y-4">
            {[
              {
                right: 'Acceso',
                description: 'Derecho a acceder a tus datos personales'
              },
              {
                right: 'Rectificación',
                description: 'Derecho a corregir datos inexactos'
              },
              {
                right: 'Eliminación',
                description: 'Derecho a solicitar la eliminación de tus datos'
              },
              {
                right: 'Portabilidad',
                description: 'Derecho a recibir tus datos en formato estructurado'
              }
            ].map((item, index) => (
              <div key={index} className="p-4 bg-white rounded-lg border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary-700">{index + 1}</span>
                  </div>
                  <h5 className="font-medium text-slate-900">{item.right}</h5>
                </div>
                <p className="text-sm text-slate-600 ml-11">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ];

  const filteredSections = privacySections.filter(section =>
    section.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    section.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 bg-gray-50">
        {/* Enhanced Header Section */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <nav className="mb-4">
              <ol className="flex items-center space-x-2 text-sm text-gray-500">
                <li>
                  <Link to="/" className="hover:text-primary-600 transition-colors">Inicio</Link>
                </li>
                <li>
                  <ChevronRight className="w-4 h-4" />
                </li>
                <li className="text-gray-900 font-medium">Política de Privacidad</li>
              </ol>
            </nav>

            <div className="max-w-3xl">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Política de Privacidad
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Tu privacidad es nuestra prioridad. Conoce cómo recopilamos, usamos y protegemos tu información.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 rounded-lg p-2">
                    <Lock className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Datos Seguros</h3>
                    <p className="text-sm text-gray-500">Encriptación de extremo a extremo</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 rounded-lg p-2">
                    <Eye className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Transparencia</h3>
                    <p className="text-sm text-gray-500">Control total sobre tus datos</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-primary-100 rounded-lg p-2">
                    <UserCheck className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Tus Derechos</h3>
                    <p className="text-sm text-gray-500">Acceso y control garantizado</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Search Bar with improved styling */}
          <div className="mb-12">
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Buscar en la política de privacidad..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-gray-900 placeholder-gray-500"
                />
              </div>
              <p className="mt-2 text-sm text-gray-500 text-center">
                Última actualización: Marzo 2024
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <nav className="lg:col-span-1">
              <div className="space-y-1">
                {filteredSections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary-50 text-primary-700'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {section.title}
                  </button>
                ))}
              </div>
            </nav>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                {privacySections.find((section) => section.id === activeSection)?.content}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
} 